import axios from '@/lib/axios';
import { stringify } from '@/utils/queryStringWrapper';
import retrieveAxiosErr from '@/utils/retrieveAxiosErr';
import { userManager } from './odUserManager';

export interface RefreshedTokens {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type?: string;
  scope?: string;
  expires_in?: number;
  expires_at?: number;
  [k: string]: unknown;
}

/**
 * Refresh an access token via the OIDC token endpoint, using the
 * `refresh_token` grant type.
 *
 * Replacement for `userManager._useRefreshToken(...)` (private internal,
 * removed in `oidc-client-ts@3.x`).
 *
 * Mirrors the existing password-grant axios call shape used by
 * `authProvider.ts` so error handling stays consistent with the rest of the
 * authProvider.
 *
 * Thrown errors have `.message === 'invalid_grant'` when the IdP rejects
 * the refresh token — the IIFE in `odUserManager.ts` keys off that string
 * to track consecutive failures.
 */
export async function refreshAccessToken(
  refreshToken: string,
  scope?: string
): Promise<RefreshedTokens> {
  try {
    const url = await userManager.metadataService.getTokenEndpoint();
    if (!url) {
      throw new Error('No token endpoint available');
    }
    const resp = await axios.post<RefreshedTokens>(
      url,
      stringify({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: 'ondata-portal',
        scope,
      }),
      { headers: { Accept: 'application/json' } }
    );
    return resp.data;
  } catch (err) {
    throw new Error(retrieveAxiosErr(err));
  }
}
